/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import '../../util/findDOMNodePolyfill'
import { ReduxProvider, createEmptyState } from '@react-page/core/lib'
import { toast } from 'react-toastify'
import { LoadingIcon, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'
import { useConfig } from '@peracto/peracto-config'
import { CREATE, UPDATE, GET_ONE, GET_LIST, useClient } from '@peracto/client'
import { SettingsProvider } from '@peracto/peracto-hooks'
import { Helmet } from 'react-helmet'
import { ThemeProvider } from 'emotion-theming'
import Config, { EditorConfigProvider } from '../../config'
import Editor from '../../service/editor'
import LibraryService from '../../service/library'
import PeractoEditable from '../Editable'
import Toolbar from '../Toolbar'
import RightPanel from '../RightPanel'
import LeftPanel from '../LeftPanel'
import PageTitle from '../PageTitle'

import { PluginDecorator } from '@peracto/peracto-content'

import '@react-page/core/lib/index.css' // we also want to load the stylesheets
import * as S from '../../styled'
import { Body } from './styled'

const PeractoEditor = ({
    defaultPlugin = null,
    plugins = {
        content: [],
        layout: [],
    },
    config = {},
    theme = {},
    pluginDecorator = PluginDecorator,
}) => {
    const conf = new Config(plugins, config)
    const { client, loading } = useClient()
    const { type, id } = useParams()

    const peractoConfig = useConfig()
    const { globalStyles, bodyStyles } = peractoConfig.get('editorConfig', null)
    const { content } = peractoConfig.get('features', {})
    const isNotSearchable = content?.searchableFalseOnCreation || false

    const [redirect, setRedirect] = useState()
    const [editor, setEditor] = useState()
    const [notFound, setNotFound] = useState(false)
    const [unauthorised, setUnauthorised] = useState(false)
    const [editorContent, setContent] = useState(createEmptyState())
    const [peractoSettings, setPeractoSettings] = useState()

    const [settings, setSettings] = useState({
        name: '',
        title: '',
        metaDescription: '',
        image: '',
        locale: '',
        shortDescription: '',
        slug: '',
        keywords: [],
        contentProductLinks: [],
        contentBabbageAccountRestriction: [],
        contentAccessRestrictions: [],
        flattenedContentAccessRestrictions: [],
        searchable: !isNotSearchable,
        backgroundImage: '',
        backgroundRepeat: '',
        backgroundSize: '',
        backgroundPosition: '',
        backgroundContain: false,
        countryCode: 'all',
    })

    let contentBodyStyle = {}

    if (settings?.backgroundImage?.length > 0) {
        contentBodyStyle = {
            ...contentBodyStyle,
            backgroundImage: `url("${settings?.backgroundImage}")`,
            backgroundRepeat: settings?.backgroundRepeat,
            backgroundSize: settings?.backgroundSize,
            backgroundPosition: settings?.backgroundPosition,
        }
    }

    const [showSettings, setShowSettings] = useState(false)

    const createEditor = data => {
        const editor = new Editor({
            defaultPlugin: defaultPlugin,
            plugins: pluginDecorator.decoratePlugins(plugins),
            editables: [data],
        })

        editor.setLibraryService(new LibraryService())

        setEditor(editor)
    }

    const onFetchData = async () => {
        if (id) {
            try {
                const { data } = await client(GET_ONE, 'contents', {
                    id: '/contents/' + id,
                })

                const { body, ...settings } = data

                const contentData = JSON.parse(body)[0]

                let flattenedContentAccessRestrictions = []

                if (settings?.contentAccessRestrictions?.length > 0) {
                    flattenedContentAccessRestrictions = settings?.contentAccessRestrictions?.map(
                        restriction => restriction.value
                    )
                }

                createEditor(contentData)
                setContent(contentData)
                setSettings({
                    ...settings,
                    flattenedContentAccessRestrictions,
                    title:
                        settings.title && settings.title.length > 0
                            ? settings.title
                            : settings.name,
                })
            } catch (e) {
                console.error(e)

                if (e.status === 403) {
                    setUnauthorised(true)
                }

                if (e.status === 404) {
                    setNotFound(true)
                }
            }
        } else {
            return Promise.resolve()
        }
    }

    useEffect(() => {
        if (typeof client === 'function') {
            if (id) {
                onFetchData()
            } else {
                createEditor(editorContent)
                setShowSettings(true) // show settings panel by default on new pages
            }
        }
    }, [client, id])

    const fetchSettings = async () => {
        try {
            const { data } = await client(GET_LIST, 'configurations', {
                id: 'configurations',
            })

            const settingsData = {
                groups: {},
                values: {},
            }
            data.forEach(row => {
                settingsData.groups = {
                    ...settingsData.groups,
                    ...row.groups,
                }

                settingsData.values = {
                    ...settingsData.values,
                    ...row.values,
                }
            })

            setPeractoSettings(settingsData)
        } catch (e) {
            console.error(e)
        }
    }

    const onSave = async (indexContent = false) => {
        const content = editor.query.editables().map(e => editor.plugins.serialize(e))
        const method = id ? UPDATE : CREATE

        if (!settings.name || settings.name.trim().length === 0) {
            toast.error('Name cannot be empty.')
            setSettings({
                ...settings,
                name: '',
            })
            return
        }

        if (type === 'template') {
            if (content?.[0]?.cells?.[0]?.rows?.length > 1) {
                return toast.warn(
                    'Templates can only support a single row. Ensure all blocks sit within a single Container before saving.'
                )
            } else {
                delete content?.[0]?.cells?.[0]?.id
                content[0].cells[0].layout = {
                    plugin: { name: 'peracto/layout/container', version: '1.0.0' },
                    state: {},
                }
            }
        }

        const headers = {}
        if (indexContent) {
            headers['peracto-index-content'] = true
        }

        const parsedSettings = { ...settings }

        if (!['article', 'page'].includes(type)) {
            delete parsedSettings.searchable
        }

        if (['element', 'template'].includes(type)) {
            delete parsedSettings.slug
        }

        if (settings?.contentProductLinks?.length > 0) {
            parsedSettings.contentProductLinks = settings?.contentProductLinks.map(
                (product, idx) => {
                    const isNew = product['@id'].includes('/products/')

                    return {
                        [isNew ? 'product' : '@id']: product['@id'],
                        productDisplayOrder: idx + 1,
                    }
                }
            )
        }

        if (settings?.contentBabbageAccountRestriction?.length > 0) {
            parsedSettings.contentBabbageAccountRestriction = settings?.contentBabbageAccountRestriction.map(
                restriction => {
                    return { babbageAccount: restriction.babbageAccount }
                }
            )
        }

        if (settings?.flattenedContentAccessRestrictions?.length > 0) {
            parsedSettings.contentAccessRestrictions = settings?.flattenedContentAccessRestrictions.map(
                restriction => {
                    const existingRestriction = settings?.contentAccessRestrictions.find(
                        existingRestriction => existingRestriction.value === restriction
                    )
                    if (existingRestriction) {
                        return existingRestriction['@id']
                    } else {
                        return {
                            type: 'NOT_USER_GROUP',
                            value: restriction,
                            content: id ? `/contents/${id}` : null,
                        }
                    }
                }
            )
        } else {
            parsedSettings.contentAccessRestrictions = []
        }

        try {
            const { data } = await client(
                method,
                'contents',
                {
                    data: {
                        ...parsedSettings,
                        title:
                            parsedSettings.title.length > 0
                                ? parsedSettings.title
                                : parsedSettings.name,
                        type: parsedSettings.type || type,
                        locale: parsedSettings.locale || 'en_GB',
                        body: [JSON.stringify(content)],
                    },
                    id: id ? `/contents/${id}` : null,
                },
                headers
            )

            if (data.violations && data.violations.length > 0) {
                if (data.violations[0].propertyPath === 'name') {
                    setShowSettings(true)
                    toast.error(data.violations[0].message)
                } else {
                    toast.error(data.violations.map(err => err.message).join('\n'))
                }
            } else {
                if (method === CREATE) {
                    setRedirect(`/content/edit/${data.type}/${data.originId}`)
                } else {
                    window.location.href = `/content/edit/${data.type}/${data.originId}`
                }
                toast.success('Content successfully saved!')
                setShowSettings(false)
            }
        } catch (e) {
            console.error(e)

            if (e?.error?.body?.violations?.length > 0) {
                if (e?.error?.body?.violations[0].propertyPath === 'name') {
                    setShowSettings(true)
                    toast.error(e.error.body.violations[0].message)
                } else {
                    toast.error(e.error.body.violations.map(err => err.message).join('\n'))
                }
            }
        }
    }

    useEffect(() => {
        if (!loading && typeof client === 'function') {
            fetchSettings()
        }
    }, [loading, client])

    if (!editor) {
        if (notFound) {
            return (
                <S.LayoutWrapper>
                    <NotFoundMessage
                        url={`/${type}s`}
                        message={`The ${type} you're looking for could not be found`}
                        buttonLabel={`Go to ${type}s`}
                    />
                </S.LayoutWrapper>
            )
        } else if (unauthorised) {
            return (
                <S.LayoutWrapper>
                    <UnauthorisedMessage />
                </S.LayoutWrapper>
            )
        } else {
            return (
                <S.LayoutWrapper>
                    <S.Header>
                        <PageTitle
                            title="Loading..."
                            showSettings={showSettings}
                            setShowSettings={setShowSettings}
                            contentType={type}
                        />
                    </S.Header>
                    <S.BodyLoading>
                        <LoadingIcon />
                    </S.BodyLoading>
                </S.LayoutWrapper>
            )
        }
    }

    return (
        <>
            {redirect && <Redirect to={redirect} />}

            <Helmet>
                {id ? (
                    <title>{settings.name} | Editing | Peracto</title>
                ) : (
                    <title>Add Content | Peracto</title>
                )}
            </Helmet>

            <ThemeProvider theme={theme}>
                <EditorConfigProvider config={conf}>
                    <SettingsProvider settings={peractoSettings}>
                        <ReduxProvider store={editor.store}>
                            <S.LayoutWrapper>
                                <S.Header>
                                    <PageTitle
                                        title={settings.name}
                                        showSettings={showSettings}
                                        setShowSettings={setShowSettings}
                                        contentType={type}
                                    />
                                    <Toolbar onSave={onSave} contentId={id} />
                                </S.Header>
                                <S.Wrapper>
                                    <LeftPanel
                                        settings={settings}
                                        setSettings={setSettings}
                                        showSettings={showSettings}
                                        contentType={type}
                                    />
                                    <Body
                                        id="editor-content-body"
                                        className="!font-body"
                                        style={bodyStyles || {}}
                                    >
                                        {globalStyles ? (
                                            <div css={globalStyles} data-testid="editor-body">
                                                <div style={contentBodyStyle}>
                                                    <PeractoEditable
                                                        editor={editor}
                                                        id={editorContent.id}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <PeractoEditable
                                                editor={editor}
                                                id={editorContent.id}
                                            />
                                        )}
                                    </Body>
                                    <RightPanel editor={editor} plugins={plugins} />
                                </S.Wrapper>
                            </S.LayoutWrapper>
                        </ReduxProvider>
                    </SettingsProvider>
                </EditorConfigProvider>
            </ThemeProvider>
        </>
    )
}

export default PeractoEditor
